import Select from "react-select";
import { useEffect, useMemo } from "react";
import { useOutlet } from "../../context/OutletContext";
import { selectStyles } from "../../utils/cssUtils";
import { useStock } from "../../context/StockProvider";

const SelectSearchInput = ({
  selectedOutlet,
  setSelectedOutlet,
  token,
  compact = false,
}) => {
  const { outletOptions, defaultOutlet, fullOutlets, loading, error } = useOutlet();
  const { fetchStockData } = useStock();

  const options = outletOptions.map((name) => ({
    label: name,
    value: name,
  }));

  // Restore a previously-selected outlet on mount, falling back to default
  useEffect(() => {
    if (selectedOutlet) return;

    const stored = localStorage.getItem("selectedOutlet");

    if (stored) {
      setSelectedOutlet(stored);
    } else if (defaultOutlet) {
      setSelectedOutlet(defaultOutlet);
    }
  }, [defaultOutlet, selectedOutlet, setSelectedOutlet]);

  // Persist selection whenever it changes
  useEffect(() => {
    if (selectedOutlet) {
      localStorage.setItem("selectedOutlet", selectedOutlet);
    }
  }, [selectedOutlet]);

  const handleChange = (selectedOption) => {
    setSelectedOutlet(selectedOption.value);
  };

  const getCurrentValue = () => {
    if (selectedOutlet) {
      return options.find((opt) => opt.value === selectedOutlet);
    }

    if (defaultOutlet) {
      return options.find((opt) => opt.value === defaultOutlet);
    }

    return null;
  };

  // Sync the rest of the outlet's details into localStorage
  useEffect(() => {
    if (!selectedOutlet || !fullOutlets?.length) return;

    fetchStockData(token, selectedOutlet);

    const outletObj = fullOutlets.find(
      (o) => o.name === selectedOutlet
    );

    if (outletObj) {
      const {
        address,
        company,
        logo,
        name,
        pan_no,
        phone_no,
      } = outletObj;

      const setSanitizedItem = (key, value) => {
        const sanitized =
          typeof value === "string" && value.trim() !== ""
            ? value.trim()
            : "";
        localStorage.setItem(key, sanitized);
      };

      setSanitizedItem("selectedAddress", address);
      setSanitizedItem("selectedCompany", company);
      setSanitizedItem("selectedLogo", logo);
      setSanitizedItem("selectedName", name);
      setSanitizedItem("selectedPan", pan_no);
      setSanitizedItem("selectedPhone", phone_no);
    }
  }, [selectedOutlet, fullOutlets, fetchStockData, token]);

  const styles = useMemo(() => {
    if (!compact) {
      return {
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
        }),
      };
    }

    return {
      ...selectStyles,
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
    };
  }, [compact]);

  return (
    <div className="select-search w-[300px]">
      {loading ? (
        <p className="text-center">Loading outlets...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <Select
          value={getCurrentValue()}
          isSearchable
          placeholder="Select Outlet"
          onChange={handleChange}
          options={options}
          isLoading={loading}
          className="z-40"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={styles}
        />
      )}
    </div>
  );
};

export default SelectSearchInput;